<template>
	<div class="db2-feed">
		<div class="db2-feed-head">
			<div class="db2-feed-heading">
				<v-icon v-if="isEditMode" small class="db2-card-drag" @mousedown.native.stop="onDragStart">mdi-drag</v-icon>
				<span>{{ card.name || 'Live Alerts' }}</span>
			</div>
			<router-link v-if="alertsPath" class="db2-card-link" :to="alertsPath">View all ({{ totalCount }}) →</router-link>
			<a v-else class="db2-card-link" href="#">View all ({{ totalCount }}) →</a>
		</div>
		<div class="db2-feed-list">
			<div class="db2-feed-item" v-for="a in items" :key="a.id">
				<div class="icon" :class="a.cls">
					<v-icon size="26">{{ a.icon }}</v-icon>
				</div>
				<div class="body">
					<div class="top">
						<span class="sev" :class="a.cls">{{ a.label }}</span>
						<span class="when">{{ a.when }}</span>
					</div>
					<div class="title">{{ a.title }}</div>
					<div class="place" v-if="a.place">{{ a.place }}</div>
				</div>
			</div>
			<div class="db2-feed-empty" v-if="!items.length">No active alerts</div>
		</div>
	</div>
</template>

<script>
import { HTTPClient } from '../../../../assets/js/http';
import { mockAlerts } from '../mockPointData';
let client = new HTTPClient();

/**
 * Relative time label matching the Live Alerts mock.
 * @param {string|number|Date} ts
 * @returns {string}
 */
function relTime(ts) {
	if (!ts) return '';
	const ms = Date.now() - new Date(ts).getTime();
	const m = Math.max(0, Math.round(ms / 60000));
	if (m < 1) return 'just now';
	if (m < 60) return m + ' min ago';
	const h = Math.round(m / 60);
	if (h < 48) return h + 'h ago';
	return Math.round(h / 24) + 'd ago';
}

/**
 * Severity chip, icon, and CSS class for an alert type.
 * @param {string} type
 * @returns {{ cls: string, label: string, icon: string }}
 */
function severityMeta(type) {
	if (type === 'critical') return { cls: 'is-crit', label: 'Critical', icon: 'mdi-shield-alert' };
	if (type === 'warning') return { cls: 'is-warn', label: 'Warning', icon: 'mdi-alert' };
	if (type === 'alert') return { cls: 'is-alert', label: 'Alert', icon: 'mdi-alert' };
	return { cls: 'is-info', label: 'Info', icon: 'mdi-information-variant' };
}

/**
 * Real active-alert list for the dashboard default site.
 */
export default {
	name: 'AlertsCard',
	props: {
		card: { type: Object, required: true },
		isEditMode: { type: Boolean, default: false },
		timePeriod: { type: String, default: 'week' },
		siteId: { type: String, default: null },
		projectId: { type: String, default: null },
		refreshKey: { type: Number, default: 0 },
		variant: { type: String, default: 'card' }
	},
	data() {
		return { alerts: [] };
	},
	computed: {
		/**
		 * Site used for the query.
		 * @returns {string|null}
		 */
		querySiteId() {
			return (this.card.config && this.card.config.siteId) || this.siteId || null;
		},
		/**
		 * Rows for the list.
		 * @returns {Array}
		 */
		items() {
			const limit = (this.card.config && this.card.config.limit) || 8;
			const mapped = (this.alerts || []).slice(0, limit).map(a => {
				const type = (a.alertConfig && a.alertConfig.alertType) || 'info';
				const zone = a.measurement && a.measurement.zone;
				const sev = severityMeta(type);
				return {
					id: a.objectId,
					title: (a.alertConfig && a.alertConfig.message) || (a.measurement && a.measurement.name) || 'Alert',
					place: (zone && zone.name) || '',
					when: relTime(a.createdAt),
					cls: sev.cls,
					label: sev.label,
					icon: sev.icon
				};
			});
			if (mapped.length) return mapped;
			return mockAlerts().slice(0, limit);
		},
		/**
		 * Total alert count for the header link.
		 * @returns {number}
		 */
		totalCount() {
			if (this.alerts && this.alerts.length) return this.alerts.length;
			return this.items.length;
		},
		/**
		 * Alerts page route.
		 * @returns {object|null}
		 */
		alertsPath() {
			if (!this.projectId || !this.querySiteId) return null;
			return { path: `/projects/${this.projectId}/sites/${this.querySiteId}/alerts` };
		}
	},
	watch: {
		querySiteId: { immediate: true, handler() { this.load(); } },
		refreshKey() { this.load(); }
	},
	methods: {
		/**
		 * Start a canvas move from the in-card title handle.
		 * @param {MouseEvent} e
		 */
		onDragStart(e) {
			if (!this.isEditMode) return;
			this.$emit('drag-start', e);
		},
		/**
		 * Fetch active alerts.
		 */
		async load() {
			if (!this.querySiteId) {
				this.alerts = [];
				return;
			}
			try {
				const results = await client.robustQuery(`/api/sites/${this.querySiteId}/alerts?isActive=true`);
				this.alerts = (results && results.alerts) || [];
			} catch (e) {
				console.error(e);
				this.alerts = [];
			}
		}
	}
};
</script>
